import React, { useCallback, useMemo } from 'react';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import type { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import IntervalTextInput from '../Inputs/IntervalTextInput';
import SelectDayCalendar from '../Inputs/SelectDayCalendar';
import SelectDayWeek from '../Inputs/SelectDayWeek/SelectDayWeek';
import SelectMonth from '../Inputs/SelectMonth/SelectMonth';
import SelectPosition from '../Inputs/SelectPosition/SelectPosition';
import { YearlyBy, type AllRepeatDetails } from '../Repeat.types';

interface RepeatYearlyProps {
  value: AllRepeatDetails;
  onChange: (value: AllRepeatDetails) => void;
  enableYearlyInterval?: boolean;
  radioValue: YearlyBy;
  setRadioValue: (value: YearlyBy) => void;
  inputSize: 'small' | 'medium';
  inputVariant: TextFieldProps['variant'];
}

const RepeatYearly = ({
  value,
  onChange,
  enableYearlyInterval,
  radioValue,
  setRadioValue,
  inputSize = 'small',
  inputVariant,
}: RepeatYearlyProps) => {
  const maxDaysInMonth = useMemo(() => {
    if (value.byMonth && value.byMonth.length > 0) {
      const month = value.byMonth[0];
      // always return 29 for February for safety
      if (month === 2) {
        return 29;
      }
      const year = new Date().getFullYear();
      // the 0 gets the last day of the previous month and byMonth is 1 based
      return new Date(year, month, 0).getDate();
    }
    return 31;
  }, [value]);
  const disabledOnBYSETPOS = radioValue === YearlyBy.BYMONTH;
  const disabledOnBYMONTH = radioValue === YearlyBy.BYSETPOS;

  const handleRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const radioVal = e.target.value as YearlyBy;
      setRadioValue(radioVal);
      if (radioVal === YearlyBy.BYMONTH) {
        onChange({
          ...value,
          bySetPos: [],
          byDay: [],
          byMonth: [],
        });
      } else {
        onChange({ ...value, byMonthDay: [], byMonth: [] });
      }
    },
    [onChange, value, setRadioValue],
  );

  const handleOnChange = useCallback(
    (allRepeatDetails: AllRepeatDetails) => {
      onChange({ ...allRepeatDetails });
      if (radioValue !== YearlyBy.BYMONTH) {
        setRadioValue(YearlyBy.BYMONTH);
      }
    },
    [onChange, radioValue, setRadioValue],
  );

  const handleOnTheChange = useCallback(
    (allRepeatDetails: AllRepeatDetails) => {
      onChange({ ...allRepeatDetails });
      if (radioValue !== YearlyBy.BYSETPOS) {
        setRadioValue(YearlyBy.BYSETPOS);
      }
    },
    [onChange, radioValue, setRadioValue],
  );

  return (
    <Stack direction="column" spacing={2} sx={{ alignItems: 'flex-start' }}>
      {enableYearlyInterval && (
        <IntervalTextInput
          value={value}
          onChange={onChange}
          unit="year"
          pluralizeUnit
          inputSize={inputSize}
          inputVariant={inputVariant}
        />
      )}
      <RadioGroup name="Yearly" value={radioValue} onChange={handleRadioChange}>
        <Stack direction="column" spacing={2} sx={{ alignItems: 'flex-start' }}>
          <Stack direction="row" spacing={4} sx={{ alignItems: 'center' }}>
            <Radio
              slotProps={{
                input: {
                  value: YearlyBy.BYMONTH,
                  name: 'day',
                },
              }}
            />
            <Typography
              sx={{
                color: disabledOnBYMONTH ? 'text.disabled' : 'text.primary',
                fontSize: inputSize,
              }}
            >
              On
            </Typography>
            <SelectMonth
              value={value}
              onChange={handleOnChange}
              disabled={disabledOnBYMONTH}
              inputVariant={inputVariant}
              inputSize={inputSize}
            />
            <SelectDayCalendar
              maxDaysInMonth={maxDaysInMonth}
              value={value}
              onChange={handleOnChange}
              disabled={disabledOnBYMONTH}
              inputVariant={inputVariant}
              inputSize={inputSize}
            />
          </Stack>
          <Stack direction="row" spacing={4} sx={{ alignItems: 'center' }}>
            <Radio
              slotProps={{
                input: {
                  value: YearlyBy.BYSETPOS,
                  name: 'day',
                },
              }}
            />
            <Typography
              sx={{
                color: disabledOnBYSETPOS ? 'text.disabled' : 'text.primary',
                fontSize: inputSize,
              }}
            >
              On The
            </Typography>
            <SelectPosition
              value={value}
              onChange={handleOnTheChange}
              disabled={disabledOnBYSETPOS}
              inputVariant={inputVariant}
              inputSize={inputSize}
            />
            <SelectDayWeek
              value={value}
              onChange={handleOnTheChange}
              disabled={disabledOnBYSETPOS}
              inputVariant={inputVariant}
              inputSize={inputSize}
            />
            <Typography
              sx={{
                color: disabledOnBYSETPOS ? 'text.disabled' : 'text.primary',
                fontSize: inputSize,
              }}
            >
              of
            </Typography>
            <SelectMonth
              value={value}
              onChange={handleOnTheChange}
              disabled={disabledOnBYSETPOS}
              inputVariant={inputVariant}
              inputSize={inputSize}
            />
          </Stack>
        </Stack>
      </RadioGroup>
    </Stack>
  );
};
export default RepeatYearly;
